import React, { useState, useEffect } from 'react';
import { Globe, Smartphone, Laptop, Trash2, LogOut, Shield } from 'lucide-react';
import useSettingsStore from '../../../store/settingsStore';
import { formatDistanceToNow } from 'date-fns';

const SessionManagement = () => {
  const { sessions, loadSessions, revokeSession, revokeAllSessions, isLoading } = useSettingsStore();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [password, setPassword] = useState('');

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const getDeviceIcon = (device) => {
    if (device?.toLowerCase().includes('mobile')) {
      return <Smartphone className="h-4 w-4 sm:h-5 sm:w-5" />;
    }
    return <Laptop className="h-4 w-4 sm:h-5 sm:w-5" />;
  };

  const getLocationFlag = (location) => {
    return <Globe className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />;
  };

  const handleRevokeAll = async () => {
    if (!password) {
      alert('Please enter your password to confirm');
      return;
    }

    try {
      await revokeAllSessions(password);
      setShowConfirmModal(false);
      setPassword('');
    } catch (error) {}
  };

  const currentSession = sessions?.find(s => s.isCurrent) || {
    id: 'current',
    device: navigator.platform,
    browser: 'Current Session',
    location: 'Current Location',
    lastActive: new Date(),
    isCurrent: true,
  };

  const otherSessions = sessions?.filter(s => !s.isCurrent) || [];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center">
        <div className="p-1.5 sm:p-2 bg-primary-50 rounded-lg mr-2 sm:mr-3">
          <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
        </div>
        <div>
          <h3 className="text-sm sm:text-lg font-semibold text-gray-800">Session Management</h3>
          <p className="text-[11px] sm:text-sm text-gray-500">Manage your active sessions across devices</p>
        </div>
      </div>

      {/* Current Session */}
      <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-5 border border-primary-100/30">
        <h4 className="text-xs sm:text-sm font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
          <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
          Current Session
        </h4>
        <div className="p-3 sm:p-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg border border-primary-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start sm:items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-white rounded-lg shadow-neomorph-inset flex-shrink-0">
                {getDeviceIcon(currentSession.device)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-800 text-sm sm:text-base">This device</p>
                <p className="text-xs sm:text-sm text-gray-600 break-words">
                  {currentSession.browser} • {currentSession.os}
                </p>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                  {getLocationFlag(currentSession.location)}
                  <span className="text-[10px] sm:text-xs text-gray-500">{currentSession.location}</span>
                  <span className="text-[10px] sm:text-xs text-gray-500 hidden sm:inline">•</span>
                  <span className="text-[10px] sm:text-xs text-gray-500">
                    Last active: {formatDistanceToNow(new Date(currentSession.lastActive))} ago
                  </span>
                </div>
              </div>
            </div>
            <span className="text-[10px] sm:text-xs bg-green-100 text-green-700 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-medium w-fit">
              Current
            </span>
          </div>
        </div>
      </div>

      {/* Other Sessions */}
      {otherSessions.length > 0 && (
        <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-5 border border-primary-100/30">
          <h4 className="text-xs sm:text-sm font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
            <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
            Other Active Sessions ({otherSessions.length})
          </h4>
          
          <div className="space-y-2 sm:space-y-3">
            {otherSessions.map((session) => (
              <div
                key={session.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-white transition-all border border-transparent hover:border-gray-200 hover:shadow-neomorph-hover"
              >
                <div className="flex items-start sm:items-center space-x-2 sm:space-x-3 min-w-0">
                  <div className="p-1.5 sm:p-2 bg-white rounded-lg shadow-neomorph-inset flex-shrink-0">
                    {getDeviceIcon(session.device)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-800 text-sm sm:text-base break-words">
                      {session.device || 'Unknown Device'}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600 break-words">
                      {session.browser} • {session.os}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                      {getLocationFlag(session.location)}
                      <span className="text-[10px] sm:text-xs text-gray-500">{session.location}</span>
                      <span className="text-[10px] sm:text-xs text-gray-500 hidden sm:inline">•</span>
                      <span className="text-[10px] sm:text-xs text-gray-500">
                        Last active: {formatDistanceToNow(new Date(session.lastActive))} ago
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => revokeSession(session.id)}
                  disabled={isLoading}
                  className="p-1.5 sm:p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all self-start sm:self-center"
                  title="Revoke session"
                >
                  <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-primary-100/50">
            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={isLoading}
              className="w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg shadow-md shadow-red-500/30 hover:shadow-lg transition-all flex items-center justify-center text-sm"
            >
              <LogOut className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
              Sign out of all other devices
            </button>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-2 ml-0.5 sm:ml-1">
              This will sign you out of all active sessions except this one.
            </p>
          </div>
        </div>
      )}

      {/* Confirmation Modal - Mobile Responsive */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-[#E8ECF5] rounded-xl shadow-neomorph p-4 sm:p-6 max-w-md w-full mx-auto border border-primary-100/30">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Confirm Sign Out</h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
              To sign out of all other devices, please enter your password to confirm.
            </p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 mb-3 sm:mb-4 text-sm"
              autoFocus
            />
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setPassword('');
                }}
                className="px-3 sm:px-4 py-2 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleRevokeAll}
                disabled={isLoading || !password}
                className="px-3 sm:px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg shadow-md shadow-red-500/30 hover:shadow-lg transition-all disabled:opacity-50 text-sm"
              >
                {isLoading ? 'Signing out...' : 'Sign out'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default SessionManagement;